/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/heading-has-content */
import { Markdown, MarkdownOptions } from "@moe/priv/components/markdown";
import { SingleMessage } from "@moe/priv/model/message";
import { cn, Names, templateNames } from "@moe/priv/utils";
import { useDataContext } from "@web/route-services/chat/data-context";
import { remarkQuotes } from "@web/route-services/chat/message/remark-quotes";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { useAppContext } from "@web/route-services/root/AppContext";
import { Element } from "hast";
import { State } from "mdast-util-to-hast";
import { useCallback, useEffect, useMemo, useRef } from "react";
import remarkBreaks from "remark-breaks";

interface Props {
  charName?: string;
  message: SingleMessage;
  editText: string;
  setEditText: (text: string) => void;
  isEditing: boolean;
}

/**
 * Messsage text display component.
 * Renders the message text with a custom markdown renderer.
 * Allows for the message to be edited.
 */
export function Display({ charName, message, isEditing, editText, setEditText }: Props) {
  const { service } = useServiceContext();
  const { persona, chars } = useDataContext();
  const { isTouchDevice } = useAppContext();
  const isUser = message.kind === "user";
  const editDivRef = useRef<HTMLDivElement | null>(null);
  const { settings } = useAppContext();

  // Determine the character name to use for {{char}} templating.
  // Priority: 1) Provided via props (e.g. CharacterMessage) 2) First character in chat 3) Fallback to "unknown".
  const defaultCharName = charName ?? Array.from(chars.values())[0]?.name ?? "unknown";

  /**
   * Focus on the editable div when it's mounted
   */
  const focusCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    editDivRef.current = node;
    node.focus();
    // Create a text node if the div is empty to ensure Firefox has something to select
    if (!node.firstChild) {
      node.appendChild(document.createTextNode(""));
    }
    const selection = window.getSelection();
    const range = document.createRange();

    // Select the last child node
    range.selectNodeContents(node.lastChild || node);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  /**
   * There's a bug with on firefox where copy and pasting text between contenteditable elements doesn't work as expected.
   * Issue: https://stackoverflow.com/questions/51758564/paste-fix-for-contenteditable
   * Fix: https://stackoverflow.com/questions/76896489/how-to-fix-default-paste-behavior-of-content-editable-div
   */
  useEffect(() => {
    if (!isEditing) return;
    const el = editDivRef.current;
    if (!el) return;
    el.addEventListener("paste", (e) => {
      e.preventDefault();
      const text = (e.clipboardData || (window as any).clipboardData).getData("text/plain");
      const selection = window.getSelection();
      if (!selection) return;
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      selection.removeAllRanges();
      setEditText(el.innerText);
    });
  }, [isEditing, setEditText]);

  const options = useMemo(() => {
    const plugins: any[] = [remarkBreaks];
    if (!settings?.blockquoteMode) plugins.push(remarkQuotes);
    return {
      remarkPlugins: plugins,
      remarkToRehypeOptions: {
        handlers: {
          emphasis
        }
      },
      rehypeReactOptions: {
        passNode: true,
        components: getComponents(isUser)
      }
    };
  }, [isUser, settings?.blockquoteMode]);

  const preFormat = (text: string) => {
    console.log("blockquoteMode", settings?.blockquoteMode);
    const names: Names = {
      user: persona.name,
      char: defaultCharName
    };
    text = templateNames(text, names).trim();
    // Normalize windows style linebreaks to unix style line breaks
    text = text.replace(/\r\n/g, "\n");
    // Replace newlines with a nonbreaking space + newline + space to prevent line breaks from collapsing
    text = text.replace(/(\n)(?![*-])\n/g, "$1&nbsp;\n ");
    // Undo the newline conversions if we're in a code block
    text = text.replace(/[\s\S]*?/g, (m) => m.replace(/&nbsp;\n /g, "\n"));
    // Turn quotes into block quotes
    if (settings?.blockquoteMode) {
      text = text.replace(/"([^"]*)"(?:\s*)/g, "\n> $1\n\n");
    }
    return text;
  };

  return (
    <>
      {isEditing ? (
        <div
          ref={focusCallbackRef}
          className="w-full whitespace-pre-wrap bg-transparent text-left [overflow-wrap:anywhere] focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isTouchDevice) {
              service.submitEdit(message, editText);
              e.preventDefault();
            }
          }}
          onInput={(e) => {
            setEditText((e.target as HTMLDivElement).innerText);
          }}
          contentEditable={true}
        >
          {message.text}
        </div>
      ) : (
        <Markdown options={options}>{preFormat(message.text)}</Markdown>
      )}
    </>
  );
}

/**
 * Defines a custom blockquote handler for remark-rehype.
 * The handler definition is unchanged from the default,
 * except we also pass along the node metadata.
 */
function emphasis(state: State, node: any) {
  const result: Element = {
    type: "element",
    tagName: "em",
    properties: {},
    data: {
      // Pass along the node metadata
      meta: node?.meta
    },
    children: state.wrap(state.all(node), true)
  };

  state.patch(node, result);
  return state.applyData(node, result);
}

function getComponents(isUser: boolean) {
  return {
    q: (props: any) => (
      <q
        {...props}
        className={`font-semibold ${isUser ? "text-chat-user-message-text-quotes" : "text-chat-character-message-text-quotes"}`}
      />
    ),
    h1: (props: any) => <h1 {...props} className="my-4 text-3xl font-bold [overflow-wrap:anywhere]" />,
    h2: (props: any) => <h2 {...props} className="my-3 text-2xl font-bold [overflow-wrap:anywhere]" />,
    h3: (props: any) => <h3 {...props} className="my-2 text-xl font-bold [overflow-wrap:anywhere]" />,
    em: (props: any) => {
      const isQuote = (props?.node?.data as any)?.meta?.isQuote;
      if (isQuote) {
        return (
          <span
            {...props}
            className={`font-semibold ${isUser ? "text-chat-user-message-text-quotes" : "text-chat-character-message-text-quotes"}`}
          />
        );
      }
      return (
        <em
          {...props}
          className={`font-semibold italic ${
            isUser ? "text-chat-user-message-text-italics" : "text-chat-character-message-text-italics"
          }`}
        />
      );
    },
    strong: (props: any) => <strong {...props} className="font-bold" />,
    p: (props: any) => <p {...props} className="[overflow-wrap:anywhere]" />,
    a: (props: any) => (
      <a {...props} className="text-action-primary font-semibold brightness-110 hover:underline" target="_blank" />
    ),
    code: (props: any) => (
      <code {...props} className="bg-container-secondary inline-block w-fit rounded px-2 py-1.5 font-medium" />
    ),
    pre: (props: any) => (
      <pre
        {...props}
        className="bg-container-secondary my-2 whitespace-pre-wrap rounded-md p-2 [overflow-wrap:anywhere]"
      />
    ),
    ul: (props: any) => <ul {...props} className="my-2 list-inside list-disc" />,
    ol: (props: any) => <ol {...props} className="my-2 list-inside list-decimal" />,
    blockquote: (props: any) => {
      return (
        <blockquote
          {...props}
          className={cn(
            "my-2 inline-block rounded-[2px] border-l-[4px] pl-3 font-semibold",
            isUser
              ? "text-chat-user-message-text-quotes border-text-chat-user-message-text-quotes"
              : "text-chat-character-message-text-quotes border-chat-character-message-text-quotes"
          )}
        >
          {props.children}
        </blockquote>
      );
    },
    img: (props: any) => (
      <img {...props} className="max-h-96 w-fit rounded-lg object-contain shadow-lg" draggable={false} />
    ),
    span: (props: any) => {
      return <span {...props} className={cn("whitespace-pre-wrap [overflow-wrap:anywhere]", props.className)} />;
    }
  };
}
